import { useEffect, useState } from 'react';
import type { ProjectSummary } from '@todo/shared';
import { ApiError } from '../api/client';
import { projectApi } from '../api/project.api';
import { useAuth } from '../hooks/useAuth';

/**
 * Tag WHICH PROJECT the work belongs to - the Task create and edit forms
 * both render this, shaped after DepartmentSelect. Always optional: a task
 * without a project is fine. The inline "New project" flow is offered only
 * to admins (the API enforces the role regardless); everyone else just picks
 * from what exists.
 */
interface ProjectSelectProps {
  /** Shows the * and drops the "(optional)" hint. */
  required?: boolean;
  value: string | undefined;
  /** projectName rides along so callers can e.g. prefix the task title. */
  onChange: (projectId: string | undefined, projectName?: string) => void;
  error?: string;
  disabled?: boolean;
}

const NEW_SENTINEL = '__new__';

export function ProjectSelect({ required = false, value, onChange, error, disabled }: ProjectSelectProps) {
  const { user } = useAuth();
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const canCreate =
    user?.role.systemKey === 'ADMIN' || user?.role.systemKey === 'SUPER_ADMIN';

  useEffect(() => {
    let cancelled = false;
    projectApi
      .list()
      // Same guard as DepartmentSelect: a blank id would render as
      // <option value=""> - indistinguishable from "No project".
      .then((rows) => { if (!cancelled) setProjects(rows.filter((p) => p.id && p.id.trim() !== '')); })
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function createProject() {
    const name = newName.trim();
    if (!name) return;
    setCreateBusy(true);
    setCreateError(null);
    try {
      const project = await projectApi.create({ name });
      setProjects((current) =>
        [...current, project].sort((a, b) => a.name.localeCompare(b.name)),
      );
      onChange(project.id, project.name);
      setCreating(false);
      setNewName('');
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Could not create the project');
    } finally {
      setCreateBusy(false);
    }
  }

  const selected = projects.find((p) => p.id === value);

  return (
    <div>
      <label htmlFor="project-select" className="block text-sm font-medium text-slate-700">
        Project{' '}
        {required
          ? <span className="ml-0.5 text-red-500">*</span>
          : <span className="ml-1 text-slate-400">(optional)</span>}
      </label>

      {creating ? (
        <div className="mt-1.5 space-y-2">
          <input
            autoFocus
            value={newName}
            onChange={(e) => { setNewName(e.target.value); setCreateError(null); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); void createProject(); }
            }}
            placeholder="Project name"
            maxLength={120}
            className={[
              'w-full rounded-lg border px-3 py-2 text-sm outline-none transition placeholder:text-slate-400 focus:ring-2',
              createError
                ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                : 'border-slate-300 focus:border-brand-500 focus:ring-brand-100',
            ].join(' ')}
          />
          {createError && <p className="text-xs text-red-600">{createError}</p>}
          <div className="flex gap-2">
            <button type="button" disabled={createBusy || !newName.trim()}
              onClick={() => void createProject()}
              className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-700 disabled:opacity-50">
              {createBusy ? 'Creating...' : 'Create project'}
            </button>
            <button type="button"
              onClick={() => { setCreating(false); setNewName(''); setCreateError(null); }}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-100">
              Back
            </button>
          </div>
        </div>
      ) : (
        <select
          id="project-select"
          value={value ?? ''}
          disabled={disabled || loading}
          onChange={(e) => {
            if (e.target.value === NEW_SENTINEL) {
              setCreating(true);
              return;
            }
            const picked = projects.find((p) => p.id === e.target.value);
            onChange(e.target.value || undefined, picked?.name);
          }}
          aria-invalid={error ? true : undefined}
          className={[
            'mt-1.5 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none transition focus:ring-2',
            'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
            error
              ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
              : 'border-slate-300 focus:border-brand-500 focus:ring-brand-100',
          ].join(' ')}
        >
          <option value="">{loading ? 'Loading projects...' : (required ? 'Choose a project' : 'No project')}</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>{project.name}</option>
          ))}
          {canCreate && <option value={NEW_SENTINEL}>+ New project…</option>}
        </select>
      )}
      {selected?.description && !creating && (
        <p className="mt-1 line-clamp-2 text-xs text-slate-500">{selected.description}</p>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
